import React, { useEffect, useState } from 'react';
import { DriveItem } from '../types';
import { getGoogleToken } from '../services/api';

// Google API client ID - should be set in environment variables in production
const CLIENT_ID = '93321750063-c48hf718bcgepehuvidq55qoacjdi3h3.apps.googleusercontent.com';

interface GoogleDrivePickerProps {
  onFilesPicked: (files: DriveItem[]) => void;
}

const GoogleDrivePicker: React.FC<GoogleDrivePickerProps> = ({ onFilesPicked }) => {
  const [pickerApiLoaded, setPickerApiLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load Google API client library
    const loadGapiClient = () => {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.gapi.load('client:picker', () => {
          setPickerApiLoaded(true);
        });
      };
      document.body.appendChild(script);
    };

    loadGapiClient();
  }, []);

  // Function to show the Google Drive picker
  const showPicker = async () => {
    if (!pickerApiLoaded) {
      setError('Google Picker API not loaded yet');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get Google access token from our backend
      const { accessToken } = await getGoogleToken();
      
      if (!accessToken) {
        throw new Error('Failed to get Google access token');
      }

      // Define the allowed MIME types with specific file types rather than wildcards
      const allowedMimeTypes = [
        // Common video formats
        'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/webm', 'video/mpeg',
        
        // Common image formats
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff',
        'image/heic', 'image/heif', // Apple HEIC/HEIF formats
        
        // Common audio formats
        'audio/mpeg',               // MP3
        'audio/mp4', 'audio/m4a',  // M4A, AAC
        'audio/wav', 'audio/wave', 'audio/x-wav',  // WAV variants
        'audio/ogg', 'audio/opus', // OGG, OPUS
        'audio/webm',              // WEBM audio
        'audio/aac',               // AAC
        'audio/flac',              // FLAC
        'audio/x-m4a',             // Alternative M4A
        'audio/x-aiff', 'audio/aiff', // AIFF
        'audio/basic',             // AU, SND
        'audio/midi', 'audio/x-midi', // MIDI
        'audio/x-matroska',        // MKA (Matroska Audio)
        'audio/x-ms-wma',          // Windows Media Audio
        
        // Document formats
        'application/pdf',   // PDF documents
        'text/plain',        // Plain text
        'text/markdown',     // Markdown
        'text/html',         // HTML documents
        'application/rtf',   // Rich Text Format
        
        // Google formats
        'application/vnd.google-apps.document',    // Google Docs
        'application/vnd.google-apps.kix',         // Alternative Google Docs format
        'application/vnd.google-apps.video',       // Google Meet recordings
        'application/vnd.google-apps.audio',       // Google audio files
        'application/vnd.google-apps.photo',       // Google photos
        'application/vnd.google-apps.folder'       // Google Drive folders
      ].join(',');

      // Create and render a Picker for selecting files
      const docsView = new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setMode(window.google.picker.DocsViewMode.LIST)
        .setMimeTypes(allowedMimeTypes);
        
      const picker = new window.google.picker.PickerBuilder()
        .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setAppId(CLIENT_ID.split('-')[0])
        .setOAuthToken(accessToken)
        .addView(docsView)
        .addView(new window.google.picker.DocsUploadView().setMimeTypes(allowedMimeTypes))
        .setCallback(pickerCallback)
        .build();
      picker.setVisible(true);
    } catch (err: any) {
      console.error('Error showing picker:', err);
      setError(err.message || 'Failed to open Google Drive Picker');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle picker selection
  const pickerCallback = (data: any) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const files: DriveItem[] = data.docs.map((doc: any) => ({
        id: doc.id,
        name: doc.name,
        mimeType: doc.mimeType
      }));
      onFilesPicked(files);
    } else if (data.action === window.google.picker.Action.CANCEL) {
      console.log('User canceled the file picker');
    }
  };

  return (
    <div className="google-drive-picker">
      <button 
        className="button"
        onClick={showPicker}
        disabled={!pickerApiLoaded || isLoading}
      >
        {isLoading ? 'Loading...' : 'Select Files from Google Drive'}
      </button>
      
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default GoogleDrivePicker;

// Add type definitions for the Google API
declare global {
  interface Window {
    gapi: {
      load: (api: string, callback: () => void) => void;
    };
    google: {
      picker: {
        View: any;
        DocsView: any;
        DocsViewMode: {
          LIST: string;
          GRID: string;
        };
        ViewId: { DOCS: string; FOLDERS: string; };
        Feature: { NAV_HIDDEN: string; MULTISELECT_ENABLED: string };
        Action: { PICKED: string; CANCEL: string };
        PickerBuilder: any;
        DocsUploadView: any;
      };
    };
  }
} 